import { SEED_CLIENTS } from './governedMcpSynthetic';
import type { QuotaDirectory, QuotaUser } from './governedMcpTypes';
import { currentQuotaMonth, isQuotaMonth } from './quotaUsage';

const FALLBACK_PEOPLE = [
  { id: 'u-priya', name: 'Priya Shah', email: 'priya.shah@acme.financial', roleId: 'r-lead', roleName: 'Lead', departmentId: 'd-risk' },
  { id: 'u-marcus', name: 'Marcus Chen', email: 'marcus.chen@acme.financial', roleId: 'r-ops', roleName: 'Operations', departmentId: 'd-claims' },
  { id: 'u-elena', name: 'Elena Rossi', email: 'elena.rossi@acme.financial', roleId: 'r-counsel', roleName: 'Counsel', departmentId: 'd-legal' },
  { id: 'u-james', name: 'James Okonkwo', email: 'james.okonkwo@acme.financial', roleId: 'r-analyst', roleName: 'Analyst', departmentId: 'd-kyc' },
  { id: 'u-aisha', name: 'Aisha Rahman', email: 'aisha.rahman@acme.financial', roleId: 'r-lead', roleName: 'Lead', departmentId: 'd-data' },
  { id: 'u-daniel', name: 'Daniel Park', email: 'daniel.park@acme.financial', roleId: 'r-pm', roleName: 'Product', departmentId: 'd-product' },
  { id: 'u-sofia', name: 'Sofia Alvarez', email: 'sofia.alvarez@acme.financial', roleId: 'r-ops', roleName: 'Operations', departmentId: 'd-claims' },
  { id: 'u-noah', name: 'Noah Williams', email: 'noah.williams@acme.financial', roleId: 'r-eng', roleName: 'Engineer', departmentId: 'd-platform' },
];

const FALLBACK_DEPARTMENTS = [
  { id: 'd-risk', name: 'Risk' },
  { id: 'd-claims', name: 'Claims' },
  { id: 'd-legal', name: 'Legal' },
  { id: 'd-kyc', name: 'KYC' },
  { id: 'd-data', name: 'Data Science' },
  { id: 'd-product', name: 'Product' },
  { id: 'd-platform', name: 'Platform' },
];

const FALLBACK_ROLES = [
  { id: 'r-lead', name: 'Lead' },
  { id: 'r-ops', name: 'Operations' },
  { id: 'r-counsel', name: 'Counsel' },
  { id: 'r-analyst', name: 'Analyst' },
  { id: 'r-pm', name: 'Product' },
  { id: 'r-eng', name: 'Engineer' },
];

const MODELS: [string, number][][] = [
  [['Claude 3.5 Sonnet', 0.46], ['GPT-4o', 0.32], ['Internal Agent', 0.22]],
  [['GPT-4o', 0.54], ['Claude 3.5 Sonnet', 0.28], ['Gemini 2.0 Flash', 0.18]],
  [['Gemini 2.0 Flash', 0.48], ['GPT-4o', 0.33], ['Internal Agent', 0.19]],
  [['Internal Agent', 0.44], ['Claude 3.5 Sonnet', 0.31], ['GPT-4o', 0.25]],
  [['GPT-4o', 0.7], ['Internal Agent', 0.3]],
];

function hash(value: string): number {
  let next = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    next ^= value.charCodeAt(i);
    next = Math.imul(next, 16777619);
  }
  return next >>> 0;
}

function monthDays(month: string): string[] {
  if (!isQuotaMonth(month)) return [];
  const [year, monthPart] = month.split('-').map(Number);
  const last = new Date(year, monthPart, 0).getDate();
  const today = new Date();
  const current = currentQuotaMonth();
  const maxDay = month === current ? Math.max(1, today.getDate()) : last;
  return Array.from({ length: maxDay }, (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`);
}

export type StatisticsOption = { id: string; name: string };

export type StatisticsUniverse = {
  users: QuotaUser[];
  clients: StatisticsOption[];
  departments: StatisticsOption[];
  roles: StatisticsOption[];
  people: StatisticsOption[];
};

export function buildSyntheticStatisticsUsers(
  month: string,
  liveClients: { id: string; name: string }[],
  directory: QuotaDirectory
): StatisticsUniverse {
  const clients = (liveClients.length ? liveClients : SEED_CLIENTS).map((item) => ({ id: item.id, name: item.name }));
  const departments = (directory.departments.length
    ? directory.departments.map((item) => ({ id: item.id, name: item.name, matchIds: item.matchIds }))
    : FALLBACK_DEPARTMENTS.map((item) => ({ ...item, matchIds: [item.id] }))
  ).filter((item, index, list) => list.findIndex((entry) => entry.id === item.id) === index);
  const roles = (directory.roles.length ? directory.roles : FALLBACK_ROLES).filter(
    (item, index, list) => list.findIndex((entry) => entry.id === item.id) === index
  );

  const people = (directory.users.length ? directory.users.slice(0, 10) : FALLBACK_PEOPLE).map((item, index) => {
    const assignedDept =
      departments.find((department) => department.matchIds.includes(item.departmentId) || department.id === item.departmentId) ||
      departments[index % Math.max(departments.length, 1)];
    const assignedRole = roles.find((role) => role.id === item.roleId) || roles[index % Math.max(roles.length, 1)];
    return {
      id: item.id,
      name: item.name,
      email: 'email' in item ? item.email : '',
      roleId: item.roleId || assignedRole?.id || '',
      roleName: ('roleName' in item && item.roleName) || assignedRole?.name || 'Role',
      departmentId: item.departmentId || assignedDept?.id || '',
    };
  });

  const days = monthDays(month);
  const users: QuotaUser[] = [];

  people.forEach((person, personIndex) => {
    const clientCount = 1 + (hash(`${person.id}-clients`) % Math.min(2, clients.length));
    for (let slot = 0; slot < clientCount; slot += 1) {
      const client = clients[(personIndex + slot * 3) % clients.length];
      const seed = hash(`${person.id}-${client.id}-${month}`);
      const tokenLimit = [400_000, 750_000, 1_000_000, 1_500_000, 2_000_000, 3_000_000][seed % 6];
      const usedRatio = 0.18 + ((seed >> 5) % 95) / 100;
      const tokensUsed = Math.round(tokenLimit * Math.min(1.18, usedRatio));
      const mix = MODELS[seed % MODELS.length];
      const mixTotal = mix.reduce((sum, [, share]) => sum + share, 0);
      const weights = days.map((_, index) => {
        const wave = 0.55 + 0.45 * Math.sin((index + (seed % 9)) * 0.55);
        return wave * (0.7 + ((seed >> (index % 8)) % 5) * 0.08);
      });
      const weightTotal = weights.reduce((sum, weight) => sum + weight, 0) || 1;
      users.push({
        id: `demo-${person.id}-${client.id}-${month}`,
        clientId: client.id,
        userId: person.id,
        name: person.name,
        email: person.email,
        role: person.roleName,
        roleId: person.roleId,
        departmentId: person.departmentId,
        source: slot === 0 ? 'department' : 'role',
        tokenLimit,
        tokensUsed,
        dailyUsage: days.map((date, index) => ({
          date,
          tokens: Math.max(0, Math.round((weights[index] / weightTotal) * tokensUsed)),
        })),
        byModel: mix.map(([model, share]) => ({
          model,
          tokens: Math.round((share / mixTotal) * tokensUsed),
        })),
        updatedAt: `${month}-12T10:00:00.000Z`,
      });
    }
  });

  return {
    users,
    clients,
    departments: departments.map((item) => ({ id: item.id, name: item.name })),
    roles: roles.map((item) => ({ id: item.id, name: item.name })),
    people: people.map((item) => ({ id: item.id, name: item.name })),
  };
}
